import {
  cleanPath,
  isLeadingSlashPathWithoutTrailingSlash,
  LeadingSlashPath,
  LeadingSlashPathWithoutTrailingSlash,
} from '@modules/utilities/utils/path';
import { assistantPathSuffix } from '@modules/assistant/constants/assistantNavConstants';
import { coursesPathSuffix } from '@modules/learning/constants/coursesNavConstants';
import {
  availableDocsContentLocales,
  defaultLocale,
  LocaleByPathPrefix,
} from '../constants/translationResourceConstants';
import Locale from '../enums/Locale';

export const stringToLocale = (locale: string): Locale | undefined => {
  return LocaleByPathPrefix.get(locale);
};

export const defaultLocaleSlug = stringToLocale(defaultLocale) as string;

// Get user preferred locale from browser and check if it is supported by the app
// If not supported, return default locale (en-US)
export const getSupportedPreferredLocalesOnBrowser = (): Locale[] => {
  return navigator.languages
    .map((localeString) => stringToLocale(localeString.toLowerCase()))
    .filter((browserLocale) =>
      availableDocsContentLocales.some((supportedLocale) => supportedLocale === browserLocale),
    ) as Locale[];
};

/**
 * Extracts and returns locale information and subpath from a given URL path.
 *
 * @param {string} path - The URL path from which locale and subpath are to be extracted.
 * @return {object} An object containing:
 *                  - isLocalePrefixed: Boolean indicating if the path starts with a locale.
 *                  - urlLocale: The extracted locale from the path or the default locale if none is found.
 *                  - subPath: The subpath following the locale in the URL, or the entire path if no locale is found.
 */
export const getUrlData = (
  path: string,
): {
  isLocalePrefixed: boolean;
  urlLocale: Locale | undefined;
  subPath: LeadingSlashPath;
} => {
  // docsPath = path without potential '/docs' prefix (omitted in certain environments)
  const docsPath = path.startsWith('/docs') ? path.slice('/docs'.length) : path;
  if (
    availableDocsContentLocales.find((locale) => docsPath.startsWith(`/${locale.toLowerCase()}`))
  ) {
    // eslint-disable-next-line prefer-regex-literals
    const regex = new RegExp(`^/([a-z]{2}-[a-z]{2,3})/([^?]*)`);
    let match = regex.exec(docsPath);
    if (match) {
      return {
        isLocalePrefixed: true,
        urlLocale: stringToLocale(match[1]),
        subPath: `/${match[2]}`,
      };
    }
    // eslint-disable-next-line prefer-regex-literals
    const indexRegex = new RegExp(`^/([a-z]{2}-[a-z]{2,3})$`);
    match = indexRegex.exec(docsPath);

    if (match) {
      return {
        isLocalePrefixed: true,
        urlLocale: stringToLocale(match[1]),
        subPath: '/',
      };
    }
  }

  return {
    isLocalePrefixed: false,
    urlLocale: defaultLocale,
    subPath: cleanPath(docsPath) as LeadingSlashPathWithoutTrailingSlash,
  };
};

export const prefixLocaleToPath = (locale: Locale, path: LeadingSlashPathWithoutTrailingSlash) => {
  if (!isLeadingSlashPathWithoutTrailingSlash(path))
    throw Error(`Expected path to have type isLeadingSlashPathWithoutTrailingSlash. Got ${path}`);
  const localeSlug = locale.toLowerCase();
  return `/${localeSlug}${path}`;
};

export const pathsWithNoLocale = new Set([
  assistantPathSuffix,
  coursesPathSuffix,
  '/release-notes',
]);

export const shouldRedirectToTargetLocale = ({
  isLocalePrefixed,
  targetLocale,
  subPath,
}: {
  isLocalePrefixed: boolean;
  targetLocale: Locale | null;
  subPath: LeadingSlashPath;
}): boolean => {
  // Check if current URL is prefixed with a locale, and if so, no need to redirect to anywhere
  // Otherwise if there's no locale prefix, check if the target locale is different from the default EN-US locale, and if not, redirect to the target locale.
  // The last check `targetLocale !== defaultLocale` is to prevent appending en-us to the URL when the target locale is already en-US.
  return !!(
    !isLocalePrefixed &&
    targetLocale &&
    targetLocale !== defaultLocale &&
    !pathsWithNoLocale.has(subPath)
  );
};
